import axios from 'axios';
import { toast } from 'sonner';
import { formatCATDate, formatCATDateTime } from '../../utils/timezone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const esc = (v) => {
  const s = String(v ?? '');
  if (s.includes(',') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const escHtml = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
));

const downloadCSVFile = (filename, header, rows) => {
  const csv = [header, ...rows].map((r) => r.map(esc).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const openPrintWindow = (html, width = 1100, height = 800) => {
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const w = window.open(url, '_blank', `width=${width},height=${height}`);
  if (!w) {
    URL.revokeObjectURL(url);
    toast.error('Popup blocked. Please allow popups to print.');
    return;
  }
  w.focus();
  setTimeout(() => {
    w.print();
    URL.revokeObjectURL(url);
  }, 600);
};

export const downloadMonthlySummaryCSV = (claims, ym, hospLabel) => {
  if (!claims?.length) {
    toast.info('No claims to download for this month');
    return;
  }
  const header = ['Claim ID', 'Patient', 'Date', 'Serial Number', 'Hospital', 'Amount (USD)', 'Status'];
  const rows = claims.map((c) => [
    c.claim_id || '',
    c.patient_name || '',
    c.timestamp ? formatCATDate(c.timestamp) : '',
    c.patient_serial_number || '',
    c.hospital_name || '',
    (typeof c.total_claim_amount === 'number' ? c.total_claim_amount : Number(c.total_claim_amount || 0)).toFixed(2),
    c.status || '',
  ]);
  downloadCSVFile(`monthly_claims_summary_${hospLabel}_${ym || 'monthly'}.csv`.replace(/\s+/g, '_'), header, rows);
};

export const printMonthlySummary = (claims, ym, hospLabel) => {
  const [yStr, mStr] = (ym || '').split('-');
  const monthLabel = yStr && mStr
    ? new Date(Number(yStr), Number(mStr) - 1, 1).toLocaleString('default', { month: 'long', year: 'numeric' })
    : ym;

  const total = (claims || []).reduce((sum, c) => sum + Number(c.total_claim_amount || 0), 0);

  const rowsHtml = (claims || []).map((c, idx) => `
    <tr>
      <td>${idx + 1}</td>
      <td>${escHtml(c.claim_id)}</td>
      <td>${escHtml(c.patient_name)}</td>
      <td>${c.timestamp ? formatCATDate(c.timestamp) : ''}</td>
      <td>${escHtml(c.patient_serial_number)}</td>
      <td>${escHtml(c.hospital_name)}</td>
      <td style="text-align:right">${Number(c.total_claim_amount || 0).toFixed(2)}</td>
      <td>${escHtml(c.status)}</td>
    </tr>
  `).join('');

  const html = `
    <html>
      <head>
        <title>Monthly Claims Summary</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 24px; }
          h1 { font-size: 18px; margin: 0 0 6px; }
          .meta { font-size: 12px; color: #444; margin-bottom: 14px; }
          table { width: 100%; border-collapse: collapse; font-size: 12px; }
          th, td { border: 1px solid #ddd; padding: 8px; }
          th { background: #f5f5f5; text-align: left; }
          .totals { margin-top: 12px; font-weight: bold; }
        </style>
      </head>
      <body>
        <h1>Monthly Claims Summary</h1>
        <div class="meta">
          <div><strong>Month:</strong> ${escHtml(monthLabel)}</div>
          <div><strong>Hospital:</strong> ${escHtml(hospLabel)}</div>
          <div><strong>Generated:</strong> ${formatCATDateTime(new Date())} (CAT)</div>
        </div>
        <table>
          <thead>
            <tr>
              <th>#</th><th>Claim ID</th><th>Patient</th><th>Date</th><th>Serial No</th><th>Hospital</th><th>Amount (USD)</th><th>Status</th>
            </tr>
          </thead>
          <tbody>
            ${rowsHtml || `<tr><td colspan="8">No claims found for this month.</td></tr>`}
          </tbody>
        </table>
        <div class="totals">Total Amount (USD): ${total.toFixed(2)} | Total Claims: ${(claims || []).length}</div>
      </body>
    </html>
  `;
  openPrintWindow(html, 1100, 800);
};

export const downloadDailySummaryCSV = (claims, dateLabel, hospLabel) => {
  if (!claims?.length) {
    toast.info('No claims to download for this day');
    return;
  }
  const header = ['Claim ID', 'Patient', 'Time', 'Serial Number', 'Hospital', 'Amount (USD)', 'Status'];
  const rows = claims.map((c) => [
    c.claim_id || '',
    c.patient_name || '',
    c.timestamp ? formatCATDateTime(c.timestamp) : '',
    c.patient_serial_number || '',
    c.hospital_name || '',
    (typeof c.total_claim_amount === 'number' ? c.total_claim_amount : Number(c.total_claim_amount || 0)).toFixed(2),
    c.status || '',
  ]);
  downloadCSVFile(`daily_claims_summary_${hospLabel}_${dateLabel || 'daily'}.csv`.replace(/\s+/g, '_'), header, rows);
};

export const printDailySummary = (claims, date, hospLabel) => {
  const dateLabel = date
    ? new Date(date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
    : date;

  const total = (claims || []).reduce((sum, c) => sum + Number(c.total_claim_amount || 0), 0);

  const rowsHtml = (claims || []).map((c, idx) => `
    <tr>
      <td>${idx + 1}</td>
      <td>${escHtml(c.claim_id)}</td>
      <td>${escHtml(c.patient_name)}</td>
      <td>${c.timestamp ? formatCATDateTime(c.timestamp) : ''}</td>
      <td>${escHtml(c.patient_serial_number)}</td>
      <td>${escHtml(c.hospital_name)}</td>
      <td style="text-align:right">${Number(c.total_claim_amount || 0).toFixed(2)}</td>
      <td>${escHtml(c.status)}</td>
    </tr>
  `).join('');

  const html = `
    <html>
      <head>
        <title>Daily Claims Summary</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 24px; }
          h1 { font-size: 18px; margin: 0 0 6px; }
          .meta { font-size: 12px; color: #444; margin-bottom: 14px; }
          table { width: 100%; border-collapse: collapse; font-size: 12px; }
          th, td { border: 1px solid #ddd; padding: 8px; }
          th { background: #f5f5f5; text-align: left; }
          .totals { margin-top: 12px; font-weight: bold; }
        </style>
      </head>
      <body>
        <h1>Daily Claims Summary</h1>
        <div class="meta">
          <div><strong>Date:</strong> ${escHtml(dateLabel)}</div>
          <div><strong>Hospital:</strong> ${escHtml(hospLabel)}</div>
          <div><strong>Generated:</strong> ${formatCATDateTime(new Date())} (CAT)</div>
        </div>
        <table>
          <thead>
            <tr>
              <th>#</th><th>Claim ID</th><th>Patient</th><th>Time</th><th>Serial No</th><th>Hospital</th><th>Amount (USD)</th><th>Status</th>
            </tr>
          </thead>
          <tbody>
            ${rowsHtml || `<tr><td colspan="8">No claims found for this day.</td></tr>`}
          </tbody>
        </table>
        <div class="totals">Total Amount (USD): ${total.toFixed(2)} | Total Claims: ${(claims || []).length}</div>
      </body>
    </html>
  `;
  openPrintWindow(html, 900, 700);
};

const buildHospitalsWithClaims = (allHospitals, hospitalStats) => {
  const hospitalsWithClaims = allHospitals.map((h) => {
    const stats = hospitalStats[h.hospital_name] || {};
    const totalAmount = (stats.total_pending || 0) + (stats.total_paid || 0);
    const claimCount = (stats.pending_count || 0) + (stats.paid_count || 0);
    const avgClaim = claimCount > 0 ? totalAmount / claimCount : 0;
    return { name: h.hospital_name, location: h.address || 'N/A', total: totalAmount, count: claimCount, avg: avgClaim };
  });
  return hospitalsWithClaims.sort((a, b) => b.total - a.total);
};

export const downloadHospitalClaimsCSV = (allHospitals, hospitalStats, monthKey = null) => {
  if (!allHospitals?.length) {
    toast.info('No hospitals to download');
    return;
  }
  const header = ['#', 'Hospital', 'Location', 'Claims Count', 'Total (USD)', 'Average (USD)'];
  const sortedHospitals = buildHospitalsWithClaims(allHospitals, hospitalStats);
  const rows = sortedHospitals.map((h, idx) => [idx + 1, h.name || '', h.location || '', h.count, h.total.toFixed(2), h.avg.toFixed(2)]);

  const totalClaims = Object.values(hospitalStats).reduce((sum, s) => sum + (s.pending_count || 0) + (s.paid_count || 0), 0);
  const totalAmount = Object.values(hospitalStats).reduce((sum, s) => sum + (s.total_pending || 0) + (s.total_paid || 0), 0);
  const avgClaim = totalClaims > 0 ? totalAmount / totalClaims : 0;
  rows.push(['', 'TOTAL', '', totalClaims, totalAmount.toFixed(2), avgClaim.toFixed(2)]);

  const now = new Date();
  const monthStr = monthKey || `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  downloadCSVFile(`hospital_claims_${monthStr}.csv`, header, rows);
};

export const printHospitalClaims = (allHospitals, hospitalStats, monthKey = null) => {
  const sortedHospitals = buildHospitalsWithClaims(allHospitals, hospitalStats);
  const totalClaims = Object.values(hospitalStats).reduce((sum, s) => sum + (s.pending_count || 0) + (s.paid_count || 0), 0);
  const totalAmount = Object.values(hospitalStats).reduce((sum, s) => sum + (s.total_pending || 0) + (s.total_paid || 0), 0);
  const avgClaim = totalClaims > 0 ? totalAmount / totalClaims : 0;

  const now = new Date();
  const monthName = monthKey
    ? new Date(Number(monthKey.split('-')[0]), Number(monthKey.split('-')[1]) - 1, 1).toLocaleString('en-US', { month: 'long', year: 'numeric' })
    : now.toLocaleString('en-US', { month: 'long', year: 'numeric' });

  const rowsHtml = sortedHospitals.map((h, idx) => `
    <tr>
      <td>${idx + 1}</td>
      <td>${escHtml(h.name)}</td>
      <td>${escHtml(h.location)}</td>
      <td style="text-align:center; color: ${h.count > 0 ? '#7c3aed' : '#9ca3af'}">${h.count}</td>
      <td style="text-align:right; color: ${h.total > 0 ? '#2563eb' : '#9ca3af'}">
        $${h.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </td>
      <td style="text-align:right; color: ${h.avg > 0 ? '#16a34a' : '#9ca3af'}">
        $${h.avg.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </td>
    </tr>
  `).join('');

  const html = `
    <html>
      <head>
        <title>Hospital Claims - ${monthName}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 24px; }
          h1 { font-size: 20px; margin: 0 0 6px; color: #065f46; }
          .meta { font-size: 12px; color: #444; margin-bottom: 14px; }
          table { width: 100%; border-collapse: collapse; font-size: 12px; }
          th, td { border: 1px solid #ddd; padding: 8px; }
          th { background: #ecfdf5; text-align: left; color: #065f46; }
          .totals { background: #d1fae5; font-weight: bold; }
          .totals td { color: #065f46; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <h1>Hospital Claims Summary - ${monthName}</h1>
        <div class="meta">
          <div><strong>Generated:</strong> ${formatCATDateTime(new Date())} (CAT)</div>
          <div><strong>Total Hospitals:</strong> ${allHospitals.length}</div>
        </div>
        <table>
          <thead>
            <tr>
              <th style="width: 30px;">#</th>
              <th>Hospital</th>
              <th>Location</th>
              <th style="width: 60px; text-align: center;">Claims</th>
              <th style="width: 100px; text-align: right;">Total (USD)</th>
              <th style="width: 100px; text-align: right;">Avg (USD)</th>
            </tr>
          </thead>
          <tbody>
            ${rowsHtml || '<tr><td colspan="6">No hospitals found.</td></tr>'}
          </tbody>
          <tfoot>
            <tr class="totals">
              <td colspan="3"><strong>Total</strong></td>
              <td style="text-align:center"><strong>${totalClaims}</strong></td>
              <td style="text-align:right"><strong>$${totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></td>
              <td style="text-align:right"><strong>$${avgClaim.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></td>
            </tr>
          </tfoot>
        </table>
      </body>
    </html>
  `;
  openPrintWindow(html, 950, 600);
};

export const downloadHospitalPricelist = async (hospitalName, authCfg) => {
  try {
    toast.info(`Downloading pricelist for ${hospitalName}...`);
    const response = await axios.get(`${API}/admin/hospitals/${encodeURIComponent(hospitalName)}/pricelist`, authCfg);
    const { items, currency_code } = response.data;

    if (!items || items.length === 0) {
      toast.warning(`No pricelist items found for ${hospitalName}`);
      return;
    }

    const header = ['Item ID', 'Item Name', 'Item Type', 'UoM', `Cost (${currency_code || 'USD'})`];
    const rows = items.map((item) => [item.item_id, item.item_name, item.item_type, item.uom || '', item.cost?.toFixed(2) || '0.00']);
    const safeHospitalName = hospitalName.replace(/[^a-z0-9]/gi, '_');
    downloadCSVFile(`pricelist_${safeHospitalName}.csv`, header, rows);
    toast.success(`Pricelist downloaded for ${hospitalName}`);
  } catch (error) {
    toast.error(`Failed to download pricelist for ${hospitalName}`);
  }
};

export const printHospitalPricelist = async (hospitalName, authCfg) => {
  try {
    toast.info(`Loading pricelist for ${hospitalName}...`);
    const response = await axios.get(`${API}/admin/hospitals/${encodeURIComponent(hospitalName)}/pricelist`, authCfg);
    const { items, hospital_address, currency_code } = response.data;

    if (!items || items.length === 0) {
      toast.warning(`No pricelist items found for ${hospitalName}`);
      return;
    }

    const rowsHtml = items.map((item, idx) => `
      <tr>
        <td>${idx + 1}</td>
        <td>${escHtml(item.item_id)}</td>
        <td>${escHtml(item.item_name)}</td>
        <td>${escHtml(item.item_type)}</td>
        <td>${escHtml(item.uom || '-')}</td>
        <td style="text-align:right">${escHtml(currency_code || 'USD')} ${item.cost?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</td>
      </tr>
    `).join('');

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Pricelist - ${escHtml(hospitalName)}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #047857; font-size: 20px; margin-bottom: 5px; }
          h2 { color: #666; font-size: 14px; margin-top: 0; font-weight: normal; }
          .print-date { color: #999; font-size: 12px; margin-bottom: 20px; }
          table { width: 100%; border-collapse: collapse; margin-top: 15px; }
          th { background: #ecfdf5; color: #047857; padding: 10px 8px; text-align: left; border-bottom: 2px solid #047857; font-size: 12px; }
          td { padding: 8px; border-bottom: 1px solid #ddd; font-size: 11px; }
          tr:hover { background: #f9fafb; }
          .footer { margin-top: 20px; font-size: 11px; color: #666; text-align: center; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <h1>${escHtml(hospitalName)} - Pricelist</h1>
        <h2>${escHtml(hospital_address || '')}</h2>
        <div class="print-date">Generated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
        <table>
          <thead>
            <tr>
              <th style="width: 30px;">#</th>
              <th style="width: 100px;">Item ID</th>
              <th>Item Name</th>
              <th style="width: 100px;">Type</th>
              <th style="width: 80px;">UoM</th>
              <th style="width: 120px; text-align: right;">Cost (${escHtml(currency_code || 'USD')})</th>
            </tr>
          </thead>
          <tbody>
            ${rowsHtml || '<tr><td colspan="6">No items found.</td></tr>'}
          </tbody>
        </table>
        <div class="footer">Total Items: ${items.length}</div>
      </body>
      </html>
    `;
    openPrintWindow(html, 800, 600);
  } catch (error) {
    toast.error(`Failed to load pricelist for ${hospitalName}`);
  }
};

export const printFamilyClaimHistory = (familyInfo, familyClaimHistory) => {
  if (!familyInfo || familyClaimHistory.length === 0) {
    toast.error('No claim history to print');
    return;
  }

  const totalAmount = familyClaimHistory.reduce((total, claim) => total + claim.header.total_claim_amount, 0);

  const rowsHtml = familyClaimHistory.map((claim, idx) => `
    <tr>
      <td style="padding: 8px; border-bottom: 1px solid #ddd;">${idx + 1}</td>
      <td style="padding: 8px; border-bottom: 1px solid #ddd;">${claim.header.claim_id}</td>
      <td style="padding: 8px; border-bottom: 1px solid #ddd;">${claim.header.patient_name}</td>
      <td style="padding: 8px; border-bottom: 1px solid #ddd;">${claim.header.hospital_name || 'N/A'}</td>
      <td style="padding: 8px; border-bottom: 1px solid #ddd;">${formatCATDate(claim.header.timestamp)}</td>
      <td style="padding: 8px; border-bottom: 1px solid #ddd; text-align: right;">$${claim.header.total_claim_amount?.toFixed(2) || '0.00'}</td>
    </tr>
  `).join('');

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Family Claim History - ${escHtml(familyInfo.family_id)}</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 20px; max-width: 900px; margin: 0 auto; }
        h1 { color: #7c3aed; font-size: 22px; margin-bottom: 5px; }
        .subtitle { color: #666; font-size: 14px; margin-bottom: 20px; }
        .summary-box { background: #f5f3ff; border: 1px solid #c4b5fd; border-radius: 8px; padding: 15px; margin-bottom: 20px; }
        .summary-row { display: flex; justify-content: space-between; margin-bottom: 8px; }
        .summary-label { color: #6b7280; }
        .summary-value { font-weight: bold; color: #1f2937; }
        .total-amount { font-size: 24px; color: #7c3aed; font-weight: bold; }
        table { width: 100%; border-collapse: collapse; margin-top: 15px; }
        th { background: #f5f3ff; color: #7c3aed; padding: 10px 8px; text-align: left; border-bottom: 2px solid #7c3aed; font-size: 12px; text-transform: uppercase; }
        tr:hover { background: #faf5ff; }
        .footer { margin-top: 20px; font-size: 11px; color: #666; text-align: center; border-top: 1px solid #ddd; padding-top: 15px; }
        @media print { body { padding: 0; } .no-print { display: none; } }
      </style>
    </head>
    <body>
      <h1>Family Claim History</h1>
      <div class="subtitle">Family ID: ${escHtml(familyInfo.family_id)} | Principal: ${escHtml(familyInfo.principle_member_name)}</div>

      <div class="summary-box">
        <div class="summary-row">
          <span class="summary-label">Total Claims:</span>
          <span class="summary-value">${familyClaimHistory.length}</span>
        </div>
        <div class="summary-row">
          <span class="summary-label">Family Balance:</span>
          <span class="summary-value">$${familyInfo.remaining_balance?.toFixed(2) || '0.00'}</span>
        </div>
        <div class="summary-row">
          <span class="summary-label">Total Amount of All Claims:</span>
          <span class="total-amount">$${totalAmount.toFixed(2)}</span>
        </div>
      </div>

      <table>
        <thead>
          <tr>
            <th style="width: 30px;">#</th>
            <th>Claim ID</th>
            <th>Patient</th>
            <th>Hospital</th>
            <th>Date</th>
            <th style="text-align: right;">Amount</th>
          </tr>
        </thead>
        <tbody>
          ${rowsHtml || '<tr><td colspan="6" style="text-align: center; padding: 20px;">No claims found.</td></tr>'}
        </tbody>
        <tfoot>
          <tr style="background: #f5f3ff; font-weight: bold;">
            <td colspan="5" style="padding: 10px; text-align: right;">Total:</td>
            <td style="padding: 10px; text-align: right; color: #7c3aed;">$${totalAmount.toFixed(2)}</td>
          </tr>
        </tfoot>
      </table>

      <div class="footer">
        <p>Generated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
        <p>Acacia Insurance - Family Claim History Report</p>
      </div>
    </body>
    </html>
  `;
  openPrintWindow(html, 900, 700);
};
